import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import { TA_GREEN, type TABubbleFill, toTABubbleFills, toTADisplayValue } from './tripadvisor.helper';

const BubblesContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

// TA display requirements: unfilled/partial bubbles must sit on WHITE in light mode —
// the page background must never show through (transparent is only allowed in dark mode).
const Bubble = styled.span<{ $fill: TABubbleFill }>`
  display: inline-block;
  width: 1em;
  height: 1em;
  border-radius: 50%;
  border: 1.5px solid ${TA_GREEN};
  flex-shrink: 0;
  background: ${({ $fill }) => {
    if ($fill === 'full') return TA_GREEN;
    if ($fill === 'half') return `linear-gradient(to right, ${TA_GREEN} 50%, #ffffff 50%)`;
    return '#ffffff';
  }};
`;

type TripAdvisorBubblesProps = {
  /** Raw 10-100 aggregate score from Supply (mutually exclusive with `value`) */
  rawScore?: number;
  /** Display value already on the 1-5 scale (e.g. a per-review rating) */
  value?: number;
} & HTMLAttributes<HTMLSpanElement>;

export default function TripAdvisorBubbles({ rawScore, value, ...rest }: TripAdvisorBubblesProps) {
  const displayValue = value ?? (rawScore !== undefined ? toTADisplayValue(rawScore) : undefined);
  if (displayValue === undefined) return null;
  const fills = toTABubbleFills(displayValue);
  return (
    <BubblesContainer role="img" aria-label={`${Math.round(displayValue * 10) / 10} out of 5`} {...rest}>
      {fills.map((fill, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Bubble key={i} $fill={fill} />
      ))}
    </BubblesContainer>
  );
}
